import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import CLSeparator from '../../components/CLSeparator';
import CLButton from '../../components/CLButton';
import ListingListState from '../../components-shared/ListingListState';

import useDebounce from '../../hooks/useDebounce';

import { selectWebsiteId } from '../../store/slices/website';
import {
  selectListings,
  selectListingsLoading,
  selectListingsPage,
  selectListingsSearch,
  setSearch,
  setNextPage,
  selectListingsNext,
  getListings,
  setFilter,
  selectListingsFilters,
  selectListingsIsFiltersDefault,
  resetListParams,
} from '../../store/slices/listings';

import Item from './Item';
import Header from './Header';
import {
  Container, List, Footer,
} from './styled';
import ItemPlaceholder from './ItemPlaceholder';
import { getBathrooms, getBedrooms } from '../../store/slices/dictionaries';

const ListingsList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const websiteId = useSelector(selectWebsiteId);
  const listings = useSelector(selectListings);
  const isLoading = useSelector(selectListingsLoading);
  const hasNextPage = useSelector(selectListingsNext);
  const page = useSelector(selectListingsPage);
  const search = useSelector(selectListingsSearch);
  const filters = useSelector(selectListingsFilters);
  const isFiltersDefault = useSelector(selectListingsIsFiltersDefault);

  const searchDebounce = useDebounce(search, 200);

  useEffect(() => {
    dispatch(getBedrooms());
    dispatch(getBathrooms());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getListings({
      website_id: websiteId,
      page,
      search: searchDebounce,
      filters,
    }));
  }, [page, searchDebounce, filters, websiteId, dispatch]);

  const ListingsList = () => {
    if (!listings.length) {
      if (isLoading) {
        return (
          <List>
            {[1, 2, 3].map((item) => <ItemPlaceholder key={item} />)}
          </List>
        );
      }

      if (search || !isFiltersDefault) {
        return <ListingListState type="not_found" />;
      }

      return <ListingListState type="no_listings" />;
    }

    return (
      <List>
        {listings.map((item) => (
          <Item key={item.listing_id} item={item} websiteId={websiteId} />
        ))}
      </List>
    );
  };

  return (
    <Container>
      <Header
        search={search}
        filters={filters}
        isFiltersDefault={isFiltersDefault}
        onSearch={(value) => dispatch(setSearch(value))}
        onFilter={(value) => dispatch(setFilter(value))}
        onReset={() => dispatch(resetListParams())}
      />
      <CLSeparator />

      <ListingsList />

      {hasNextPage && (
        <Footer>
          <CLButton onClick={() => dispatch(setNextPage())}>{t('show_more', 'Show more')}</CLButton>
        </Footer>
      )}
    </Container>
  );
};

export default ListingsList;
